// app/(app)/customers/add.tsx (with keyboard fix)
import { Button, Input, TextArea } from "@/components/default/forms";
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import { useAppTheme } from "@/hooks/useAppTheme";
import { Area, District, locationApi, State } from "@/services/locationApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { clearError, createCustomer } from "@/store/slices/customerSlice";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

export default function AddCustomerScreen() {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { updating, error } = useAppSelector((state) => state.customers);

  // Form fields
  const [fullName, setFullName] = useState("");
  // const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [gstin, setGstin] = useState("");
  const [address, setAddress] = useState("");
  const [pincode, setPincode] = useState("");
  // const [status, setStatus] = useState<"active" | "inactive">("active");

  // Location data
  const [states, setStates] = useState<State[]>([]);
  const [selectedStateId, setSelectedStateId] = useState<number | null>(null);
  const [districts, setDistricts] = useState<District[]>([]);
  const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(
    null,
  );
  const [areas, setAreas] = useState<Area[]>([]);
  const [selectedAreaName, setSelectedAreaName] = useState("");
  const [customAreaName, setCustomAreaName] = useState("");

  const [errors, setErrors] = useState({
    fullName: "",
    // email: "",
    phone: "",
    address: "",
    pincode: "",
    state: "",
    district: "",
    area: "",
  });

  // Load states and areas on mount
  useFocusEffect(
    useCallback(() => {
      const loadStaticData = async () => {
        try {
          const [statesData, areasData] = await Promise.all([
            locationApi.getStates(),
            locationApi.getAreas(),
          ]);
          setStates(statesData);
          setAreas(areasData);
        } catch (err) {
          console.error("Failed to load location data:", err);
          Alert.alert("Error", "Failed to load location data");
        }
      };
      loadStaticData();
    }, []),
  );

  // Load districts when state changes
  useEffect(() => {
    if (selectedStateId) {
      locationApi
        .getDistrictsByState(selectedStateId)
        .then(setDistricts)
        .catch((err) => {
          console.error(err);
          setDistricts([]);
        });
    } else {
      setDistricts([]);
    }
    setSelectedDistrictId(null);
  }, [selectedStateId]);

  // Reset form on focus
  useFocusEffect(
    useCallback(() => {
      setFullName("");
      // setEmail("");
      setPhone("");
      setGstin("");
      setAddress("");
      setPincode("");
      // setStatus("active");
      setSelectedStateId(null);
      setSelectedDistrictId(null);
      setSelectedAreaName("");
      setCustomAreaName("");
      setErrors({
        fullName: "",
        // email: "",
        phone: "",
        address: "",
        pincode: "",
        state: "",
        district: "",
        area: "",
      });
      dispatch(clearError());
    }, []),
  );

  useEffect(() => {
    if (error) Alert.alert("Error", error);
  }, [error]);

  const validate = () => {
    let valid = true;
    const newErrors = {
      fullName: "",
      // email: "",
      phone: "",
      address: "",
      pincode: "",
      state: "",
      district: "",
      area: "",
    };
    if (!fullName.trim()) {
      newErrors.fullName = "Required";
      valid = false;
    }
    // if (!email.trim()) {
    //   newErrors.email = "Required";
    //   valid = false;
    // } else if (!/\S+@\S+\.\S+/.test(email)) {
    //   newErrors.email = "Invalid email";
    //   valid = false;
    // }
    if (!phone.trim()) {
      newErrors.phone = "Required";
      valid = false;
    } else if (!/^\d{10}$/.test(phone)) {
      newErrors.phone = "10 digits required";
      valid = false;
    }
    if (!address.trim()) {
      newErrors.address = "Required";
      valid = false;
    }
    if (!pincode.trim()) {
      newErrors.pincode = "Required";
      valid = false;
    } else if (!/^\d{6}$/.test(pincode)) {
      newErrors.pincode = "6 digits required";
      valid = false;
    }
    if (!selectedStateId) {
      newErrors.state = "Select state";
      valid = false;
    }
    if (!selectedDistrictId) {
      newErrors.district = "Select district";
      valid = false;
    }
    if (!selectedAreaName && !customAreaName) {
      newErrors.area = "Select or add area";
      valid = false;
    }
    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const areaName = selectedAreaName || customAreaName;
    const result = await dispatch(
      createCustomer({
        fullName,
        // email,
        phone,
        stateId: selectedStateId!,
        districtId: selectedDistrictId!,
        areaName,
        pincode,
        address,
        // status,
        gstin: gstin || undefined,
      }),
    );
    if (createCustomer.fulfilled.match(result)) {
      Alert.alert("Success", "Customer added", [
        { text: "OK", onPress: () => router.push("/(app)/customers") },
      ]);
    }
  };

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    keyboardAvoid: { flex: 1 },
    scrollContent: {
      flexGrow: 1,
      padding: spacing.lg,
      paddingBottom: spacing.xl,
    },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.lg,
    },
    inputContainer: { marginBottom: spacing.lg },
    statusContainer: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.lg,
      paddingVertical: spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    statusLabel: { fontSize: 16, fontWeight: "500", color: colors.text },
    buttonWrapper: { marginTop: "auto", paddingTop: spacing.lg },
  });

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoid}
        keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 0}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={styles.title}>Add Customer</Text>
          <Input
            label="Full Name"
            value={fullName}
            onChangeText={setFullName}
            error={errors.fullName}
            containerStyle={styles.inputContainer}
          />
          {/* <Input
            label="Email"
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            error={errors.email}
            containerStyle={styles.inputContainer}
          /> */}
          <Input
            label="Phone Number"
            value={phone}
            onChangeText={setPhone}
            keyboardType="phone-pad"
            error={errors.phone}
            containerStyle={styles.inputContainer}
          />
          <Input
            label="GSTIN (Optional)"
            value={gstin}
            onChangeText={setGstin}
            containerStyle={styles.inputContainer}
          />

          <SearchableSelect
            label="State"
            placeholder="Select state"
            options={states.map((s) => ({
              label: s.name,
              value: s.id.toString(),
            }))}
            value={selectedStateId?.toString()}
            onValueChange={(val) => setSelectedStateId(parseInt(val))}
            error={errors.state}
            containerStyle={styles.inputContainer}
          />
          {selectedStateId && (
            <SearchableSelect
              label="District"
              placeholder="Select district"
              options={districts.map((d) => ({
                label: d.name,
                value: d.id.toString(),
              }))}
              value={selectedDistrictId?.toString()}
              onValueChange={(val) => setSelectedDistrictId(parseInt(val))}
              error={errors.district}
              containerStyle={styles.inputContainer}
            />
          )}
          <SearchableSelect
            label="Area / Root"
            placeholder="Select area"
            options={areas.map((a) => ({ label: a.name, value: a.name }))}
            value={selectedAreaName}
            onValueChange={(val) => {
              setSelectedAreaName(val);
              setCustomAreaName("");
            }}
            error={errors.area}
            containerStyle={styles.inputContainer}
            clearable
          />
          <Input
            label="Or add new area"
            value={customAreaName}
            onChangeText={setCustomAreaName}
            placeholder="New area name"
            containerStyle={styles.inputContainer}
            onFocus={() => setSelectedAreaName("")}
          />

          <Input
            label="Pincode"
            value={pincode}
            onChangeText={setPincode}
            keyboardType="numeric"
            error={errors.pincode}
            containerStyle={styles.inputContainer}
          />
          <TextArea
            label="Address"
            value={address}
            onChangeText={setAddress}
            numberOfLines={3}
            error={errors.address}
            containerStyle={styles.inputContainer}
          />

          {/* <View style={styles.statusContainer}>
            <Text style={styles.statusLabel}>Status</Text>
            <Switch
              value={status === "active"}
              onValueChange={(val) => setStatus(val ? "active" : "inactive")}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={colors.background}
            />
          </View> */}
          <View style={styles.buttonWrapper}>
            <Button
              title="Save Customer"
              onPress={handleSubmit}
              loading={updating}
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}
